import { useEffect, useRef, useState } from 'react';
import type { TripItem } from '@shared/types';
import { api, ApiError } from '../lib/api';

/**
 * Paste-to-import: one box for the text the owner already wrote — the message
 * they sent a friend, their notes, an itinerary — sorted into cards server-side
 * with their words kept verbatim.
 */
export function ImportPanel({
  tripId,
  onImported,
  onCancel,
}: {
  tripId: string;
  onImported: (items: TripItem[]) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configured, setConfigured] = useState<boolean | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    api
      .importConfigured()
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(false));
    box.current?.focus();
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || text.trim().length < 20) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.importTrip(tripId, text.trim());
      onImported(result.items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sort that text.');
      setBusy(false);
    }
  }

  if (configured === false) {
    return (
      <div className="rounded-note border border-ink/12 bg-surface p-4">
        <p className="font-util text-meta text-muted">
          Paste-to-import is not set up on this deployment yet.
        </p>
        <button onClick={onCancel} className="btn-quiet mt-2 !py-1.5 !text-meta">
          Close
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-note border border-ink/12 bg-surface p-4">
      <p className="font-body text-[0.9375rem] text-ink/85">
        Paste what you already wrote — the text you sent a friend, your notes, an
        itinerary. It gets sorted into cards with your words kept as-is.
      </p>
      <textarea
        ref={box}
        className="field mt-3 min-h-[10rem] resize-y font-body"
        placeholder={'"When you go to Crested Butte — stay at the Elevation if you can swing it, the ski-in access is worth it. Secret Stash for pizza, get the Notorious F.I.G..."'}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div className="mt-3 flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || text.trim().length < 20}
          className="btn-primary disabled:opacity-50"
        >
          {busy ? 'Sorting it out…' : 'Sort it into cards'}
        </button>
        <button type="button" onClick={onCancel} className="btn-quiet">
          Cancel
        </button>
      </div>
      {error && <p className="mt-2 font-util text-meta text-[#B4462F]">{error}</p>}
    </form>
  );
}
